import { IMAGES } from "@/constants/site";

// English version of the offers — same structure as offers.js.
// `slug` is the English URL slug, `frSlug` the French canonical slug (hreflang mapping).
export const OFFERS_EN = [
  {
    slug: "marketing-strategy-diagnostic",
    frSlug: "diagnostic-marketing-strategique",
    imgClass: "scale-110",
    name: "Strategic Marketing Diagnostic",
    short: "A clear vision of your positioning and a prioritized action plan to get back on solid ground.",
    image: IMAGES.offer1,
    imageAlt: "Brand audit documents, competitive diagnosis and marketing roadmap on a work table — strategic marketing diagnostic",
    serviceType: "Strategic Marketing Diagnostic / Marketing audit",
    type: "One-off project",
    duration: "3 to 4 weeks",
    price: "Custom quote",
    accroche: "A complete marketing audit & a clear roadmap to reignite your growth.",
    subtitle: "Gain full clarity on your positioning and receive a prioritized roadmap to drive growth. Tailored strategic support based on the French Riviera (and worldwide).",
    ctaTitle: "Ready to audit your strategy?",
    ctaText: "Book a free 30-minute discovery call (no obligation). We'll discuss your current bottlenecks and determine if a strategic audit is the right next step for your business.",
    description: "An in-depth analysis of your positioning and acquisition channels to build solid foundations and prioritize your investments.",
    keyPoints: [
      "Audit of your competitors, your brand and your sales pitch",
      "Analysis of acquisition channels and the customer journey",
      "Written diagnostic + **90-day operational roadmap**",
      "Debrief session with your teams + **30 days of email follow-up included**",
    ],
    audience:
      "For SME owners and startup founders who want to stop wasting marketing budget, gain a clear ROI, and build solid foundations before scaling.",
    summary:
      "The Strategic Marketing Audit is a comprehensive evaluation of your entire ecosystem: brand, positioning, channels, and competition. In 3 to 4 weeks, move away from guesswork with clear priorities and an actionable roadmap.",
    included: [
      "Complete audit of your brand, positioning and messaging",
      "Analysis of your acquisition channels and customer journey",
      "Study of your competitive ecosystem",
      "Interviews with you and your teams",
      "Analysis of your existing data and metrics",
      "Written diagnostic + 90-day operational roadmap",
      "Debrief workshop with your teams",
      "Email follow-up and assistance for 30 days",
    ],
    process: [
      { title: "Scoping & Discovery", text: "Kick-off interview, access to your data, internal interviews and document analysis." },
      { title: "Analysis & Audit", text: "Audit of your brand, channels, customer journey and competition. Identification of strengths, weaknesses and blind spots." },
      { title: "Prioritization", text: "Prioritizing strategic initiatives by ROI and effort—collaboratively and jargon-free." },
      { title: "Debrief", text: "Handover workshop presenting your 90-day roadmap, ready to execute from day one" },
    ],
    deliverables: [
      "Comprehensive Audit Report",
      "90-day marketing roadmap",
      "Action Plan & Owner Matrix",
    ],
    seo: {
      title: "Strategic Marketing Diagnostic French Riviera | Magali Clouzet",
      description:
        "360° marketing audit for SMEs & start-ups in Nice, Cannes & Sophia Antipolis. In 3 to 4 weeks, leave with a clear, prioritized 90-day roadmap.",
    },
  },
  {
    slug: "go-to-market-launch-plan",
    frSlug: "plan-de-lancement-go-to-market",
    name: "Launch Plan & Go-To-Market",
    short: "Secure the launch of your product or service with a structured strategy, a precise timeline and optimized channels.",
    image: IMAGES.offer2,
    imageAlt: "Launch plan and Gantt chart on screen with a goals notebook — Go-To-Market preparation",
    serviceType: "Launch Plan / Go-To-Market",
    type: "Project-based engagement",
    duration: "6 to 10 weeks",
    price: "Custom quote",
    accroche: "From idea to market: secure and maximize the impact of your product or service.",
    subtitle: "De-risk and maximize the success of your product or service launch with a structured Go-To-Market strategy, a clear timeline, and targeted acquisition channels.",
    ctaTitle: "Ready to nail the launch of your new offer?",
    ctaText: "Book a free 30-minute discovery call (no obligation). We'll review your project goals and outline the steps to structure your Go-To-Market strategy.",
    description: "A complete Go-To-Market strategy to align your messages, acquisition channels and budgets before and during the launch.",
    keyPoints: [
      "Differentiating positioning, brand promise, and targeted messaging per audience",
      "Multichannel action plan, timeline and budget",
      "Operational creative briefs for your vendors (agencies, developers, designers)",
      "**Hands-on support and adjustments for 30 days post-launch**",
    ],
    audience:
      "For startup founders preparing a high-stakes launch and SME leaders introducing a new offer without risking execution missteps.",
    summary:
      "The Launch Plan transforms your market entry into a clear roadmap: positioning, core messaging, channel strategy, timeline, and KPIs. In 6 to 10 weeks, your launch is fully structured, your teams and agency partners have precise briefs, and you maximize your chances of success from day one.",
    included: [
      "Definition or refinement of your positioning and brand promise",
      "Custom messaging matrix by target persona",
      "Go-To-Market strategy: channels, sequencing and budget allocation",
      "Detailed launch calendar",
      "Ready-to-use creative briefs (for your agencies, designers, developers)",
      "Hands-on support and optimization for 30 days post-launch",
    ],
    process: [
      { title: "Immersion", text: "Deep understanding of your offer, your market, your targets and your constraints." },
      { title: "Positioning & message", text: "Workshops to solidify your differentiation and hone a pitch that resonates with your audience." },
      { title: "Go-To-Market strategy", text: "Channel selection, action sequencing, resource and budget allocation." },
      { title: "Execution plan", text: "Launch timeline, resource allocation, and tracking dashboard." },
    ],
    deliverables: [
      "Positioning guide and messaging framework by persona",
      "Go-To-Market strategy & multichannel plan",
      "Launch timeline and creative briefs for execution partners",
      "Success metrics dashboard (KPIs)",
    ],
    seo: {
      title: "Launch Plan & Go-To-Market French Riviera | Magali Clouzet",
      description:
        "Tailor-made Go-To-Market strategy & launch plan for SMEs and start-ups (Nice, Sophia Antipolis, Cannes). Secure your market entry with method.",
    },
  },
  {
    slug: "fractional-cmo-outsourced-marketing",
    frSlug: "direction-marketing-externalisee",
    name: "Outsourced Marketing Management",
    short: "A part-time marketing director (Fractional CMO) to steer your strategy and your teams on a daily basis.",
    image: IMAGES.offer3,
    imageAlt: "Consultant and business leader steering a marketing dashboard — outsourced marketing management",
    serviceType: "Fractional CMO / Outsourced Marketing Management",
    duration: "Part-time (1 to 2 days / week)",
    engagement: "3-month minimum commitment",
    price: "Custom quote",
    accroche: "Your strategic and operational right hand, without the constraints of a full-time hire.",
    subtitle: "A senior Fractional CMO to steer your strategy, build your growth engine, and lead your team day-to-day.",
    ctaTitle: "Ready to scale your marketing function?",
    ctaText: "Book a free 30-minute discovery call (no obligation). We'll review your current bottlenecks and define the right fractional cadence for your business.",
    description: "I steer your marketing strategy, your vendors and your in-house teams on a daily basis with a dedicated expert eye.",
    keyPoints: [
      "Monthly marketing review (2h), prioritised action plan, and KPI reporting",
      "End-to-end vendor management, from strategy to execution",
      "Continuous & responsive support (reply within 24h and emergency video calls)",
      "**Team upskilling: AI tool setup and integration** for your teams",
    ],
    audience:
      "For founders and SME leaders who need ongoing strategic direction and operational rigor—without adding an executive salary to the payroll.",
    summary:
      "Fractional Marketing Leadership gives you executive-level expertise on a flexible basis (1 to 2 days/week): strategic steering, agency oversight, budget allocation, and performance tracking. Get an executive-level partner without the overhead or commitment of a full-time hire.",
    processH2: "How the partnership works",
    included: [
      "Strategic marketing roadmap & cross-functional alignment",
      "Structuring and leading of your in-house teams",
      "Agency & external partner management",
      "Budget allocation & priority management",
      "Regular reporting and performance dashboards for leadership",
      "Integration of innovative tools and methods (including AI) to speed up execution",
    ],
    process: [
      { title: "Onboarding & Audit", text: "Rapid diagnostic, team & partner alignment, business goal setting." },
      { title: "Roadmap", text: "Building the quarter's priorities, aligned with your growth objectives." },
      { title: "Ongoing Execution", text: "Sprint management, project tracking, vendor coordination, and decision-making." },
      { title: "Measure & adjust", text: "Monthly reporting, results analysis and reallocation of efforts." },
    ],
    deliverables: [
      "Quarterly marketing roadmap",
      "Governance rituals & KPI dashboards",
      "Executive updates & strategic reports",
      "Team structure & vendor management framework",
    ],
    seo: {
      title: "Outsourced Marketing Management French Riviera | Magali Clouzet",
      description:
        "Part-time marketing director (Fractional CMO) for SMEs in Nice, Cannes & Sophia Antipolis. Steer your strategy and teams at a controlled cost.",
    },
  },
];
